import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AccessSettings,
  createProfileSchema,
  NO_ACCESS,
  type ProfileResponse,
  updateProfileSchema,
} from '@k-management/shared';
import { type FormEvent, useState } from 'react';
import { AccessEditor } from '../../components/AccessEditor.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { useCommands } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { queryKeys } from '../../lib/query-keys.js';

/**
 * Administrators: profiles (ruoli-modello, docs/adr/ADR-008). Levels, data
 * scopes and features given together to the users of a profile.
 */
export function ProfilesPage() {
  const profiles = useQuery({
    queryKey: queryKeys.profiles,
    queryFn: () => apiRequest<ProfileResponse[]>('/profiles'),
  });
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const current =
    selected === 'new'
      ? null
      : (profiles.data?.find((item) => item.id === selected) ?? profiles.data?.[0] ?? null);
  const editing = selected === 'new' ? 'new' : current;

  useCommands([
    {
      key: 'new',
      label: 'Nuovo profilo',
      icon: 'plus',
      primary: true,
      onClick: () => setSelected('new'),
    },
  ]);

  return (
    <section>
      <div className="page-header">
        <div>
          <h1>Profili di accesso</h1>
          <p className="muted">
            Un profilo di accesso è un insieme di livelli, dati visibili e funzioni: lo assegni agli
            utenti e ogni sua modifica vale subito per tutti loro. Le eccezioni del singolo utente
            prevalgono.
          </p>
        </div>
      </div>

      {profiles.isPending && <p className="muted">Caricamento…</p>}
      {profiles.isError && <p className="error">{profiles.error.message}</p>}
      {profiles.data && (
        <div className="split">
          <nav className="card list-nav" aria-label="Profili di accesso">
            {profiles.data.map((item) => (
              <button
                key={item.id}
                type="button"
                className={editing !== 'new' && item.id === current?.id ? 'selected' : undefined}
                onClick={() => setSelected(item.id)}
              >
                <strong>{item.name}</strong>
                <small>
                  {item.users.length === 1 ? '1 utente' : `${item.users.length} utenti`}
                  {item.description ? ` · ${item.description}` : ''}
                </small>
              </button>
            ))}
            {profiles.data.length === 0 && <p className="muted">Nessun profilo.</p>}
          </nav>
          {editing && (
            <ProfileEditor
              key={editing === 'new' ? 'new' : editing.id}
              profile={editing === 'new' ? null : editing}
              onSaved={(id) => setSelected(id)}
              onDeleted={() => setSelected(null)}
            />
          )}
        </div>
      )}
    </section>
  );
}

function ProfileEditor({
  profile,
  onSaved,
  onDeleted,
}: {
  profile: ProfileResponse | null;
  onSaved: (id: string) => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [access, setAccess] = useState<AccessSettings>(profile?.access ?? NO_ACCESS);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.profiles });
    await queryClient.invalidateQueries({ queryKey: queryKeys.users });
  };

  const save = useMutation({
    mutationFn: (body: unknown) =>
      profile
        ? apiRequest<ProfileResponse>(`/profiles/${profile.id}`, { method: 'PATCH', body })
        : apiRequest<ProfileResponse>('/profiles', { method: 'POST', body }),
    onSuccess: async (saved) => {
      await refresh();
      onSaved(saved.id);
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  const remove = useMutation({
    mutationFn: () => apiRequest<void>(`/profiles/${profile!.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setConfirmDelete(false);
      await refresh();
      onDeleted();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const body = { name, description, access };
    const result = profile
      ? validate(updateProfileSchema, body)
      : validate(createProfileSchema, body);
    setErrors(result.errors);
    if (Object.keys(result.errors).length === 0) save.mutate(body);
  };

  return (
    <form className="card stack" onSubmit={submit} noValidate>
      <div className="form-grid">
        <Field label="Nome" error={errors.name}>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Descrizione" error={errors.description}>
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>

      <AccessEditor mode="profile" value={access} onChange={setAccess} />

      {profile && (
        <p className="muted small">
          {profile.users.length === 0
            ? 'Nessun utente ha questo profilo.'
            : `Utenti con questo profilo: ${profile.users.map((user) => `${user.firstName} ${user.lastName}`).join(', ')}.`}
        </p>
      )}
      {formError && <p className="error">{formError}</p>}

      <div className="actions">
        {profile && (
          <button
            type="button"
            className="danger"
            onClick={() => setConfirmDelete(true)}
            disabled={profile.users.length > 0}
            title={
              profile.users.length > 0 ? 'Assegna prima un altro profilo ai suoi utenti' : undefined
            }
          >
            Elimina
          </button>
        )}
        <button type="submit" className="primary" disabled={save.isPending}>
          {save.isPending ? 'Salvataggio…' : profile ? 'Salva' : 'Crea profilo'}
        </button>
      </div>

      {confirmDelete && profile && (
        <ConfirmDialog
          title={`Eliminare il profilo «${profile.name}»?`}
          confirmLabel="Elimina"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>Nessun utente lo usa. L’eliminazione resta nel registro attività.</p>
        </ConfirmDialog>
      )}
    </form>
  );
}
